import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export type ListingProviderIndicatorProps = {
  label: string
  logoUrl?: string | null
  href?: string | null
  variant?: 'inline' | 'block'
  /** Shown below label in block variant (e.g. Adzuna compliance hint). */
  hint?: string | null
  className?: string
}

function ProviderBadge({ label, logoUrl }: { label: string; logoUrl?: string | null }) {
  return (
    <Badge variant="secondarySoft" className="max-w-full gap-1.5 font-normal">
      {logoUrl?.trim() ? (
        <img
          src={logoUrl.trim()}
          alt=""
          className="h-3.5 w-auto max-w-[4.5rem] shrink-0 object-contain object-left"
        />
      ) : null}
      <span className="truncate">{label}</span>
    </Badge>
  )
}

export function ListingProviderIndicator({
  label,
  logoUrl,
  href,
  variant = 'inline',
  hint,
  className,
}: ListingProviderIndicatorProps) {
  const trimmedLabel = label.trim()
  if (!trimmedLabel) return null

  const link = href?.trim() || ''
  const badge = <ProviderBadge label={trimmedLabel} logoUrl={logoUrl} />

  if (variant === 'block') {
    const inner = link ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex w-fit transition-opacity hover:opacity-90"
      >
        {badge}
      </a>
    ) : (
      <span className="inline-flex w-fit">{badge}</span>
    )
    return (
      <div className={cn('rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground', className)}>
        {inner}
        {hint?.trim() ? <p className="mt-2 text-muted-foreground">{hint.trim()}</p> : null}
      </div>
    )
  }

  const inlineClass = cn('inline-flex w-fit', className)
  if (link) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(inlineClass, 'transition-opacity hover:opacity-90')}
      >
        {badge}
      </a>
    )
  }

  return <div className={inlineClass}>{badge}</div>
}
